import { useState, useEffect, useCallback } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';

// Base URL for the DPIN API. Same-origin in production (penflow.xpertcreation.com),
// so no CORS config changes are needed there.
const API_BASE = '/api/dpin';

// Reuses whatever auth token PenFlow already stores after Pi Sign-in / login.
// Update this key if your app stores the token under a different name.
function getAuthToken() {
  return localStorage.getItem('authToken') || localStorage.getItem('token') || '';
}

function authHeaders() {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

function FileManager({ job }) {
  const [files, setFiles] = useState([]);
  const [quotaGb, setQuotaGb] = useState(0);
  const [usedGb, setUsedGb] = useState(0);
  const [replicaCount, setReplicaCount] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/jobs/${job.id}/files/`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Could not load files.');
      const data = await safeJson(res);
      setFiles(data.files);
      setQuotaGb(data.quota_gb);
      setUsedGb(data.used_gb);
      setReplicaCount(data.replica_count || 1);
    } catch (e) {
      setMsg(e.message);
    }
  }, [job.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setMsg('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/jobs/${job.id}/upload/`, {
        method: 'POST',
        headers: token ? { Authorization: `Token ${token}` } : {},
        body: formData,
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Upload failed.');
      setMsg(`Uploaded ${data.name}.`);
      load();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (name) => {
    try {
      const res = await fetch(`${API_BASE}/jobs/${job.id}/delete-file/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ filename: name }),
      });
      if (!res.ok) throw new Error('Delete failed.');
      load();
    } catch (e) {
      setMsg(e.message);
    }
  };

  const handleDownload = (name) => {
    const token = getAuthToken();
    fetch(`${API_BASE}/jobs/${job.id}/download/${encodeURIComponent(name)}/`, {
      headers: token ? { Authorization: `Token ${token}` } : {},
    })
      .then((res) => {
        if (!res.ok) throw new Error('Download failed.');
        return res.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((e) => setMsg(e.message));
  };

  return (
    <div style={{ background: '#fff', border: '0.5px solid #E3E1D9', borderRadius: 12, padding: '1rem', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <p style={{ fontWeight: 500, fontSize: 14, margin: 0 }}>
          Job #{job.id} storage{' '}
          {replicaCount > 1 && (
            <span style={{ fontSize: 11, fontWeight: 400, color: '#0F6E56', background: '#E1F5EE', padding: '2px 8px', borderRadius: 10, marginLeft: 4 }}>
              🔁 {replicaCount}x replicated
            </span>
          )}
        </p>
        <p style={{ fontSize: 12, color: '#5F5E5A', margin: 0 }}>
          {usedGb.toFixed(4)} / {quotaGb} GB used
        </p>
      </div>

      {files.length === 0 ? (
        <p style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 8 }}>No files uploaded yet.</p>
      ) : (
        <div style={{ marginBottom: 8 }}>
          {files.map((f) => (
            <div
              key={f.name}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '6px 0',
                borderBottom: '0.5px solid #E3E1D9',
                fontSize: 13,
              }}
            >
              <span>{f.name}</span>
              <span style={{ color: '#5F5E5A', display: 'flex', gap: 8, alignItems: 'center' }}>
                {(f.size_bytes / 1024).toFixed(1)} KB
                <button
                  onClick={() => handleDownload(f.name)}
                  style={{ fontSize: 12, padding: '2px 8px', border: '0.5px solid #B4B2A9', borderRadius: 4, background: 'transparent', cursor: 'pointer' }}
                >
                  Download
                </button>
                <button
                  onClick={() => handleDelete(f.name)}
                  style={{ fontSize: 12, padding: '2px 8px', border: '0.5px solid #E24B4A', color: '#A32D2D', borderRadius: 4, background: 'transparent', cursor: 'pointer' }}
                >
                  Delete
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      <label
        style={{
          display: 'inline-block',
          fontSize: 13,
          padding: '6px 12px',
          border: '0.5px solid #B4B2A9',
          borderRadius: 6,
          cursor: uploading ? 'default' : 'pointer',
          opacity: uploading ? 0.6 : 1,
        }}
      >
        {uploading ? 'Uploading...' : 'Upload file'}
        <input type="file" onChange={handleUpload} disabled={uploading} style={{ display: 'none' }} />
      </label>
      {msg && <p style={{ fontSize: 12, color: '#5F5E5A', marginTop: 6 }}>{msg}</p>}
    </div>
  );
}

function EarningsPanel() {
  const [balance, setBalance] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const [balRes, listRes] = await Promise.all([
        fetch(`${API_BASE}/withdrawals/balance/`, { headers: authHeaders() }),
        fetch(`${API_BASE}/withdrawals/`, { headers: authHeaders() }),
      ]);
      if (balRes.ok) {
        const balData = await balRes.json();
        setBalance(parseFloat(balData.available_balance_pi));
      }
      if (listRes.ok) {
        setWithdrawals(await listRes.json());
      }
    } catch (e) {
      // silent - earnings panel is supplementary
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    setSubmitting(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/withdrawals/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ amount: parseFloat(amount), pi_wallet_address: walletAddress }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.amount?.[0] || data.error || 'Could not submit request.');
      setMsg('Withdrawal request submitted.');
      setAmount('');
      setWalletAddress('');
      load();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (balance === null) return null;

  return (
    <div style={{ background: '#fff', border: '0.5px solid #E3E1D9', borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
      <p style={{ fontWeight: 500, fontSize: 15, margin: '0 0 4px' }}>My earnings</p>
      <p style={{ fontSize: 24, fontWeight: 500, margin: '0 0 12px' }}>{balance.toFixed(6)} Pi available</p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          type="number"
          step="0.000001"
          placeholder="Amount to withdraw"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          style={{ flex: 1, padding: '6px 8px', fontSize: 13, border: '0.5px solid #B4B2A9', borderRadius: 6 }}
        />
        <input
          type="text"
          placeholder="Your Pi wallet address"
          value={walletAddress}
          onChange={(e) => setWalletAddress(e.target.value)}
          style={{ flex: 2, padding: '6px 8px', fontSize: 13, border: '0.5px solid #B4B2A9', borderRadius: 6 }}
        />
      </div>
      <button
        onClick={submit}
        disabled={submitting || !amount || !walletAddress}
        style={{
          padding: '8px 16px',
          fontSize: 13,
          fontWeight: 500,
          border: 'none',
          borderRadius: 6,
          background: '#0F6E56',
          color: '#fff',
          cursor: submitting ? 'default' : 'pointer',
          opacity: submitting ? 0.7 : 1,
        }}
      >
        {submitting ? 'Submitting...' : 'Request withdrawal'}
      </button>
      {msg && <p style={{ fontSize: 12, color: '#5F5E5A', marginTop: 6 }}>{msg}</p>}

      {withdrawals.length > 0 && (
        <div style={{ marginTop: 12, borderTop: '0.5px solid #E3E1D9', paddingTop: 8 }}>
          {withdrawals.map((w) => (
            <div key={w.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', color: '#5F5E5A' }}>
              <span>{w.amount} Pi</span>
              <span>{w.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskRunner({ job }) {
  const [command, setCommand] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState('');

  const run = async () => {
    if (!command.trim()) return;
    setRunning(true);
    setMsg('');
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/jobs/${job.id}/run-task/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ command }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Task failed.');
      setResult(data);
    } catch (e) {
      setMsg(e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{ background: '#fff', border: '0.5px solid #E3E1D9', borderRadius: 12, padding: '1rem', marginBottom: 12 }}>
      <p style={{ fontWeight: 500, fontSize: 14, margin: '0 0 8px' }}>Job #{job.id} compute</p>

      <label style={{ fontSize: 12, color: '#5F5E5A', display: 'block', marginBottom: 4 }}>Command</label>
      <textarea
        value={command}
        onChange={(e) => setCommand(e.target.value)}
        placeholder="echo hello"
        rows={2}
        style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box', fontFamily: 'monospace', marginBottom: 8 }}
      />
      <button
        onClick={run}
        disabled={running || !command.trim()}
        style={{
          padding: '8px 16px',
          fontSize: 13,
          fontWeight: 500,
          border: 'none',
          borderRadius: 6,
          background: '#0F6E56',
          color: '#fff',
          cursor: running ? 'default' : 'pointer',
          opacity: running ? 0.7 : 1,
        }}
      >
        {running ? 'Running...' : 'Run'}
      </button>
      {msg && <p style={{ fontSize: 12, color: '#A32D2D', marginTop: 6 }}>{msg}</p>}

      {result && (
        <div style={{ marginTop: 8, fontSize: 12, fontFamily: 'monospace' }}>
          <p style={{ color: '#5F5E5A', margin: '0 0 4px' }}>Exit code: {result.exit_code}</p>
          {result.stdout && (
            <pre style={{ background: '#F1EFE8', padding: 8, borderRadius: 6, whiteSpace: 'pre-wrap', margin: '0 0 4px' }}>{result.stdout}</pre>
          )}
          {result.stderr && (
            <pre style={{ background: '#FAECE7', padding: 8, borderRadius: 6, whiteSpace: 'pre-wrap', margin: 0, color: '#A32D2D' }}>{result.stderr}</pre>
          )}
        </div>
      )}
    </div>
  );
}

function HelpModal({ onClose }) {
  const sectionStyle = { marginBottom: 20 };
  const headingStyle = { fontSize: 16, fontWeight: 600, margin: '0 0 8px' };
  const textStyle = { fontSize: 14, color: '#3A3935', lineHeight: 1.6, margin: '0 0 8px' };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '20px 12px', overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{ background: '#fff', borderRadius: 16, maxWidth: 640, width: '100%', padding: '1.5rem', marginTop: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>How Xpert DPIN works</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: '#5F5E5A' }}>✕</button>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>What is this?</p>
          <p style={textStyle}>
            A marketplace where people rent out spare storage space or computing power from their own
            server, and clients pay for it using Pi. It's decentralized: files and tasks run on the
            operator's own machine, not on a single central server.
          </p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>If you want to rent storage or compute</p>
          <p style={textStyle}>1. Open this page inside Pi Browser (payments only work there).</p>
          <p style={textStyle}>2. Browse the list of nodes below. Each shows its region, reliability tier, and price.</p>
          <p style={textStyle}>3. Enter how much you need — GB and days for storage, or CPU-hours for compute — and tap "Rent with Pi".</p>
          <p style={textStyle}>4. Confirm the payment in Pi Browser. Once confirmed, your rental shows under "My rentals".</p>
          <p style={textStyle}>5. For storage: upload, download, or delete files, up to your quota. For compute: type a command and run it, and see the output.</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>If you want to become an operator (earn Pi)</p>
          <p style={textStyle}>1. Scroll to "Register a node" below and fill in your details.</p>
          <p style={textStyle}>
            2. If you have your own VPS, run this on it to install the agent that actually stores files
            or runs tasks on your machine:
          </p>
          <pre style={{ background: '#F1EFE8', padding: '10px 12px', borderRadius: 8, fontSize: 12, overflowX: 'auto', margin: '0 0 8px' }}>
{`wget ${window.location.origin}/dpin-agent/install.sh
sudo bash install.sh`}
          </pre>
          <p style={textStyle}>
            3. The install script prints an endpoint and a token — paste those into the registration
            form so your files/tasks run on your own machine, not the shared demo storage.
          </p>
          <p style={textStyle}>
            4. As clients rent your node and their jobs complete, your share (after a 7% platform fee)
            becomes available under "My earnings" — request a withdrawal there anytime.
          </p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>Fees</p>
          <p style={textStyle}>
            Clients pay exactly the operator's listed rate — nothing extra. The platform takes a 7% cut
            when releasing payment to the operator, so the operator receives 93% of what was paid.
          </p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>Getting paid out (withdrawals)</p>
          <p style={textStyle}>
            Payouts to operators are currently handled manually rather than automatically, because
            Pi Network's automatic payout system isn't yet available for real (Mainnet) Pi. Request a
            withdrawal with your Pi wallet address, and it'll be reviewed and paid by hand — you'll see
            the status update once it's approved.
          </p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>Your files are backed up automatically</p>
          <p style={textStyle}>
            When you rent storage, your files aren't just kept on one machine. The platform
            automatically copies them to up to 2 other active nodes too — so if one node goes
            offline, your files are still safe on the others. You'll see a "🔁 replicated" badge
            next to your rental once backups are in place.
          </p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>Home computers vs VPS</p>
          <p style={textStyle}>
            Only nodes running on a VPS (a server with a public address) can reliably host real files
            or tasks right now. Home computers usually sit behind routers without a public address, so
            they can't yet be reached directly — support for that is planned but not built.
          </p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>A word of caution on compute</p>
          <p style={textStyle}>
            Renting out "compute" means letting clients run commands on your machine. There's no
            sandboxing yet — a command runs with the same permissions as the agent itself. Only offer
            compute on a machine you're comfortable with, and only to people you trust, until stronger
            isolation is added.
          </p>
        </div>

        <button
          onClick={onClose}
          style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

function TierBadge({ tier }) {
  const isHigh = tier === 'high';
  return (
    <span
      style={{
        background: isHigh ? '#E1F5EE' : '#F1EFE8',
        color: isHigh ? '#085041' : '#444441',
        fontSize: 12,
        padding: '2px 8px',
        borderRadius: 6,
      }}
    >
      {isHigh ? 'High reliability' : 'Standard'}
    </span>
  );
}

function NodeCard({ node, currentUsername, onRented }) {
  const [renting, setRenting] = useState(false);
  const [quantity, setQuantity] = useState('');
  const [duration, setDuration] = useState('30');
  const [statusMsg, setStatusMsg] = useState('');

  const isCompute = node.resource_type === 'compute';
  const unitPrice = isCompute
    ? parseFloat(node.price_per_cpu_hour_pi || 0)
    : parseFloat(node.price_per_gb_month_pi || 0);
  const qty = parseFloat(quantity) || 0;
  const days = parseFloat(duration) || 0;
  const estimatedPi = isCompute
    ? +(qty * unitPrice).toFixed(6)
    : +(qty * unitPrice * (days / 30)).toFixed(6);

  const isOwnNode = currentUsername && node.operator_username === currentUsername;

  const startRental = async () => {
    if (!window.Pi) {
      setStatusMsg('Open this page inside Pi Browser to pay with Pi.');
      return;
    }
    if (qty <= 0 || estimatedPi <= 0) {
      setStatusMsg('Enter a valid quantity.');
      return;
    }

    setRenting(true);
    setStatusMsg('Requesting Pi payment permission...');

    try {
      await window.Pi.authenticate(['username', 'payments'], (payment) => {
        console.log('Incomplete payment found during re-auth:', payment);
      });
    } catch (e) {
      setStatusMsg('Could not get payment permission from Pi. Please try again.');
      setRenting(false);
      return;
    }

    setStatusMsg('Creating rental request...');

    try {
      const jobRes = await fetch(`${API_BASE}/jobs/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          node: node.id,
          resource_type: node.resource_type,
          quantity: qty,
          duration_days: isCompute ? null : days,
          pi_amount: estimatedPi,
        }),
      });
      if (!jobRes.ok) throw new Error('Could not create the rental request.');
      const job = await jobRes.json();

      setStatusMsg('Waiting for Pi payment confirmation...');

      window.Pi.createPayment(
        {
          amount: estimatedPi,
          memo: `DPIN rental - ${node.name}`,
          metadata: { job_id: job.id, node_id: node.id },
        },
        {
          onReadyForServerApproval: (paymentId) => {
            // Fire-and-forget, per the established Pi payment pattern - do not await.
            fetch(`${API_BASE}/jobs/${job.id}/approve_payment/`, {
              method: 'POST',
              headers: authHeaders(),
              body: JSON.stringify({ payment_id: paymentId }),
            });
          },
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/jobs/${job.id}/complete_payment/`, {
              method: 'POST',
              headers: authHeaders(),
              body: JSON.stringify({ payment_id: paymentId, txid }),
            })
              .then((res) => {
                if (!res.ok) throw new Error('Completion failed');
                setStatusMsg('Payment complete. Rental is active.');
                setRenting(false);
                onRented();
              })
              .catch(() => {
                setStatusMsg('Payment reached Pi but completion failed. Contact support.');
                setRenting(false);
              });
          },
          onCancel: () => {
            setStatusMsg('Payment cancelled.');
            setRenting(false);
          },
          onError: (error) => {
            setStatusMsg('Payment error: ' + (error?.message || 'unknown error'));
            setRenting(false);
          },
        }
      );
    } catch (e) {
      setStatusMsg(e.message);
      setRenting(false);
    }
  };

  return (
    <div
      style={{
        background: '#fff',
        border: '0.5px solid #E3E1D9',
        borderRadius: 12,
        padding: '1rem',
      }}
    >
      <p style={{ fontWeight: 500, fontSize: 15, margin: '0 0 4px' }}>{node.name}</p>
      <p style={{ fontSize: 13, color: '#5F5E5A', margin: '0 0 8px' }}>
        {node.region} &middot; {node.resource_type}
      </p>
      <div style={{ marginBottom: 8 }}>
        <TierBadge tier={node.reliability_tier} />
      </div>
      <div style={{ borderTop: '0.5px solid #E3E1D9', paddingTop: 8, fontSize: 13, marginBottom: 8 }}>
        {node.capacity_gb && (
          <Row label="Capacity" value={`${node.capacity_gb} GB`} />
        )}
        {node.price_per_gb_month_pi && (
          <Row label="Price" value={`${node.price_per_gb_month_pi} Pi/GB/mo`} />
        )}
        {node.cpu_cores && <Row label="CPU cores" value={node.cpu_cores} />}
        <Row label="Uptime" value={`${node.uptime_percent}%`} />
        <Row label="Status" value={node.status} />
      </div>

      {isOwnNode ? (
        <p style={{ fontSize: 12, color: '#5F5E5A', margin: 0 }}>This is your own node.</p>
      ) : node.status !== 'active' ? (
        <p style={{ fontSize: 12, color: '#5F5E5A', margin: 0 }}>Node isn't active yet.</p>
      ) : (
        <div style={{ borderTop: '0.5px solid #E3E1D9', paddingTop: 8 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: '#5F5E5A', display: 'block', marginBottom: 4 }}>
                {isCompute ? 'CPU-hours' : 'Storage (GB)'}
              </label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' }}
              />
            </div>
            {!isCompute && (
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: '#5F5E5A', display: 'block', marginBottom: 4 }}>
                  Duration (days)
                </label>
                <input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' }}
                />
              </div>
            )}
          </div>
          {estimatedPi > 0 && (
            <p style={{ fontSize: 12, color: '#5F5E5A', margin: '0 0 8px' }}>Estimated cost: {estimatedPi} Pi</p>
          )}
          <button
            onClick={startRental}
            disabled={renting}
            style={{
              width: '100%',
              padding: '8px',
              fontSize: 13,
              fontWeight: 500,
              border: 'none',
              borderRadius: 6,
              background: '#0F6E56',
              color: '#fff',
              cursor: renting ? 'default' : 'pointer',
              opacity: renting ? 0.7 : 1,
            }}
          >
            {renting ? 'Processing...' : 'Rent with Pi'}
          </button>
          {statusMsg && <p style={{ fontSize: 12, color: '#5F5E5A', marginTop: 6 }}>{statusMsg}</p>}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
      <span style={{ color: '#5F5E5A' }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function RegisterNodeForm({ onRegistered }) {
  const [form, setForm] = useState({
    name: '',
    node_type: 'vps',
    resource_type: 'storage',
    region: '',
    capacity_gb: '',
    price_per_gb_month_pi: '',
    cpu_cores: '',
    price_per_cpu_hour_pi: '',
    endpoint: '',
    agent_token: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        node_type: form.node_type,
        resource_type: form.resource_type,
        region: form.region,
        capacity_gb: form.capacity_gb || null,
        price_per_gb_month_pi: form.price_per_gb_month_pi || null,
        cpu_cores: form.cpu_cores || null,
        price_per_cpu_hour_pi: form.price_per_cpu_hour_pi || null,
        endpoint: form.endpoint || null,
        agent_token: form.agent_token || null,
      };
      const res = await fetch(`${API_BASE}/nodes/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const detail = await safeJson(res).catch(() => ({}));
        throw new Error(detail.detail || 'Could not register the node.');
      }
      const created = await safeJson(res);
      onRegistered(created);
      setForm({
        name: '',
        node_type: 'vps',
        resource_type: 'storage',
        region: '',
        capacity_gb: '',
        price_per_gb_month_pi: '',
        cpu_cores: '',
        price_per_cpu_hour_pi: '',
        endpoint: '',
        agent_token: '',
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    fontSize: 14,
    border: '0.5px solid #B4B2A9',
    borderRadius: 6,
    boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 13, color: '#5F5E5A', display: 'block', marginBottom: 4 };

  return (
    <div style={{ background: '#F1EFE8', border: '0.5px solid #E3E1D9', borderRadius: 12, padding: '1.25rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>Node name</label>
          <input style={inputStyle} value={form.name} onChange={update('name')} placeholder="My Frankfurt VPS" />
        </div>
        <div>
          <label style={labelStyle}>Node type</label>
          <select style={inputStyle} value={form.node_type} onChange={update('node_type')}>
            <option value="vps">VPS / server</option>
            <option value="computer">Home computer</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Region</label>
          <input style={inputStyle} value={form.region} onChange={update('region')} placeholder="Frankfurt" />
        </div>
        <div>
          <label style={labelStyle}>Resource type</label>
          <select style={inputStyle} value={form.resource_type} onChange={update('resource_type')}>
            <option value="storage">Storage</option>
            <option value="compute">Compute</option>
            <option value="both">Storage + compute</option>
          </select>
        </div>
        {(form.resource_type === 'storage' || form.resource_type === 'both') && (
          <>
            <div>
              <label style={labelStyle}>Capacity (GB)</label>
              <input style={inputStyle} type="number" value={form.capacity_gb} onChange={update('capacity_gb')} placeholder="100" />
            </div>
            <div>
              <label style={labelStyle}>Price per GB per month (Pi)</label>
              <input style={inputStyle} type="number" step="0.001" value={form.price_per_gb_month_pi} onChange={update('price_per_gb_month_pi')} placeholder="0.010" />
            </div>
          </>
        )}
        {(form.resource_type === 'compute' || form.resource_type === 'both') && (
          <>
            <div>
              <label style={labelStyle}>CPU cores</label>
              <input style={inputStyle} type="number" value={form.cpu_cores} onChange={update('cpu_cores')} placeholder="4" />
            </div>
            <div>
              <label style={labelStyle}>Price per CPU-hour (Pi)</label>
              <input style={inputStyle} type="number" step="0.001" value={form.price_per_cpu_hour_pi} onChange={update('price_per_cpu_hour_pi')} placeholder="0.005" />
            </div>
          </>
        )}
      </div>

      {(form.resource_type === 'compute' || form.resource_type === 'both') && (
        <p style={{ fontSize: 12, color: '#A32D2D', margin: '0 0 12px' }}>
          Compute nodes run whatever commands clients send. Only register a compute node on a
          machine you're comfortable exposing to arbitrary code execution, and make sure your
          agent's port is firewalled to only accept connections from the marketplace server.
        </p>
      )}

      <div style={{ borderTop: '0.5px solid #B4B2A9', paddingTop: 12, marginBottom: 12 }}>
        <p style={{ fontSize: 12, color: '#5F5E5A', margin: '0 0 8px' }}>
          Running your own VPS agent? Enter its details below so files get stored on your machine
          instead of the shared demo storage. Leave blank if you don't have an agent set up yet.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Agent endpoint</label>
            <input style={inputStyle} value={form.endpoint} onChange={update('endpoint')} placeholder="http://your-vps-ip:9001" />
          </div>
          <div>
            <label style={labelStyle}>Agent token</label>
            <input style={inputStyle} value={form.agent_token} onChange={update('agent_token')} placeholder="from install script output" />
          </div>
        </div>
      </div>
      {error && <p style={{ color: '#A32D2D', fontSize: 13, marginBottom: 8 }}>{error}</p>}
      <button
        onClick={submit}
        disabled={submitting || !form.name || !form.region}
        style={{
          width: '100%',
          padding: '10px',
          fontSize: 14,
          fontWeight: 500,
          border: 'none',
          borderRadius: 6,
          background: '#0F6E56',
          color: '#fff',
          cursor: submitting ? 'default' : 'pointer',
          opacity: submitting ? 0.7 : 1,
        }}
      >
        {submitting ? 'Registering...' : 'Register node'}
      </button>
    </div>
  );
}

export default function DpinMarketplace() {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [myJobs, setMyJobs] = useState([]);
  const [showHelp, setShowHelp] = useState(false);

  const loadMyJobs = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/jobs/`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await safeJson(res);
      setMyJobs(data);
    } catch (e) {
      // silent - rentals list is supplementary, not critical path
    }
  }, []);

  const loadNodes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (resourceFilter) params.set('resource_type', resourceFilter);
      if (tierFilter) params.set('reliability_tier', tierFilter);
      const res = await fetch(`${API_BASE}/nodes/?${params.toString()}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Could not load nodes.');
      const data = await safeJson(res);
      setNodes(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [resourceFilter, tierFilter]);

  useEffect(() => {
    loadNodes();
  }, [loadNodes]);

  useEffect(() => {
    loadMyJobs();
  }, [loadMyJobs]);

  const regionCount = new Set(nodes.map((n) => n.region)).size;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif' }}>
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button
          onClick={() => setShowHelp(true)}
          style={{
            padding: '6px 14px',
            fontSize: 13,
            fontWeight: 500,
            border: '0.5px solid #B4B2A9',
            borderRadius: 20,
            background: '#fff',
            cursor: 'pointer',
          }}
        >
          ❓ How it works
        </button>
      </div>
      <EarningsPanel />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: '1.5rem' }}>
        <StatCard label="Active nodes" value={nodes.filter((n) => n.status === 'active').length} />
        <StatCard label="Total nodes" value={nodes.length} />
        <StatCard label="Regions covered" value={regionCount} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Browse nodes</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={resourceFilter} onChange={(e) => setResourceFilter(e.target.value)} style={{ padding: '6px 8px' }}>
            <option value="">All resources</option>
            <option value="storage">Storage</option>
          </select>
          <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} style={{ padding: '6px 8px' }}>
            <option value="">All tiers</option>
            <option value="high">High reliability</option>
            <option value="standard">Standard</option>
          </select>
        </div>
      </div>

      {loading && <p style={{ color: '#5F5E5A' }}>Loading nodes...</p>}
      {error && <p style={{ color: '#A32D2D' }}>{error}</p>}

      {!loading && !error && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: '2rem' }}>
          {nodes.length === 0 ? (
            <p style={{ color: '#5F5E5A', fontSize: 14, gridColumn: '1/-1' }}>No nodes match these filters.</p>
          ) : (
            nodes.map((n) => (
              <NodeCard
                key={n.id}
                node={n}
                currentUsername={localStorage.getItem('username')}
                onRented={() => {
                  loadNodes();
                  loadMyJobs();
                }}
              />
            ))
          )}
        </div>
      )}

      {myJobs.length > 0 && (
        <>
          <h3 style={{ margin: '0 0 12px' }}>My rentals</h3>
          {myJobs
            .filter((j) => j.status === 'active')
            .map((j) => (
              <div key={j.id}>
                {(j.resource_type === 'storage' || j.resource_type === 'both') && <FileManager job={j} />}
                {(j.resource_type === 'compute' || j.resource_type === 'both') && <TaskRunner job={j} />}
              </div>
            ))}
          {myJobs.filter((j) => j.status === 'active').length === 0 && (
            <p style={{ fontSize: 13, color: '#5F5E5A', marginBottom: '1.5rem' }}>
              No active rentals yet.
            </p>
          )}
        </>
      )}

      <h3 style={{ margin: '0 0 12px' }}>Register a node</h3>
      <RegisterNodeForm onRegistered={loadNodes} />
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{ background: '#F1EFE8', borderRadius: 8, padding: '1rem' }}>
      <p style={{ fontSize: 13, color: '#5F5E5A', margin: '0 0 4px' }}>{label}</p>
      <p style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>{value}</p>
    </div>
  );
}
